/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AllSchoolsSubscriptionPaymentsResponse } from '../models/AllSchoolsSubscriptionPaymentsResponse';
import type { BlockSubscriptionRequest } from '../models/BlockSubscriptionRequest';
import type { ChangeSubscriptionPlanRequest } from '../models/ChangeSubscriptionPlanRequest';
import type { Country } from '../models/Country';
import type { ExtendSubscriptionGraceExpiryRequest } from '../models/ExtendSubscriptionGraceExpiryRequest';
import type { MakeDashboardSubscriptionPaymentRequest } from '../models/MakeDashboardSubscriptionPaymentRequest';
import type { PaginatedResponseSubscriptionDataResponse } from '../models/PaginatedResponseSubscriptionDataResponse';
import type { SubscriptionDashboardData } from '../models/SubscriptionDashboardData';
import type { SubscriptionExportDataResponse } from '../models/SubscriptionExportDataResponse';
import type { SubscriptionInvoiceItemResponse } from '../models/SubscriptionInvoiceItemResponse';
import type { SubscriptionInvoiceResponse } from '../models/SubscriptionInvoiceResponse';
import type { SubscriptionPaymentDetailsResponse } from '../models/SubscriptionPaymentDetailsResponse';
import type { SubscriptionPlanResponse } from '../models/SubscriptionPlanResponse';
import type { SubscriptionPricingModelResponse } from '../models/SubscriptionPricingModelResponse';
import type { SubscriptionResponse } from '../models/SubscriptionResponse';
import type { SubscriptionSessionResponse } from '../models/SubscriptionSessionResponse';
import type { UpdateSubscriptionPricingModelRequest } from '../models/UpdateSubscriptionPricingModelRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class SubscriptionControllerService {
  /**
   * @returns SubscriptionPricingModelResponse OK
   * @throws ApiError
   */
  public static changeSubscriptionPricingModel({
    requestBody,
  }: {
    requestBody: UpdateSubscriptionPricingModelRequest;
  }): CancelablePromise<SubscriptionPricingModelResponse> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/dashboard/subscription/pricing-model',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns any OK
   * @throws ApiError
   */
  public static pauseSubscription({
    requestBody,
  }: {
    requestBody: BlockSubscriptionRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/dashboard/subscription/pause',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns any OK
   * @throws ApiError
   */
  public static extendSubscriptionGrace({
    requestBody,
  }: {
    requestBody: ExtendSubscriptionGraceExpiryRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/dashboard/subscription/extend-grace',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns any OK
   * @throws ApiError
   */
  public static changeSubscriptionPlan({
    requestBody,
  }: {
    requestBody: ChangeSubscriptionPlanRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/dashboard/subscription/change-plan',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns any OK
   * @throws ApiError
   */
  public static recordPayment({
    requestBody,
  }: {
    requestBody: MakeDashboardSubscriptionPaymentRequest;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/dashboard/subscription/record-payment',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns PaginatedResponseSubscriptionDataResponse OK
   * @throws ApiError
   */
  public static getSubscriptions({
    sessionId,
    termDefinitionId,
    countryCode,
    search,
    pricingModel,
    planType,
    status,
    pageNumber,
    pageSize = 10,
  }: {
    sessionId: string;
    termDefinitionId: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
    countryCode: string;
    search?: string;
    pricingModel?:
      | 'STANDARD_PER_STUDENT'
      | 'SPLIT'
      | 'NON_STANDARD_PER_STUDENT'
      | 'FLAT_FEE_PER_TERM'
      | 'FLAT_FEE_PER_SESSION';
    planType?: 'FREE' | 'STARTER' | 'STANDARD' | 'PREMIUM';
    status?: 'ACTIVE' | 'EXPIRED';
    pageNumber?: number;
    pageSize?: number;
  }): CancelablePromise<PaginatedResponseSubscriptionDataResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription',
      query: {
        sessionId: sessionId,
        termDefinitionId: termDefinitionId,
        countryCode: countryCode,
        search: search,
        pricingModel: pricingModel,
        planType: planType,
        status: status,
        page_number: pageNumber,
        page_size: pageSize,
      },
    });
  }

  /**
   * @returns SubscriptionSessionResponse OK
   * @throws ApiError
   */
  public static getSubscriptionSessions({
    schoolId,
  }: {
    schoolId?: string;
  }): CancelablePromise<Array<SubscriptionSessionResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/sessions',
      query: {
        schoolId: schoolId,
      },
    });
  }

  /**
   * @returns SubscriptionPaymentDetailsResponse OK
   * @throws ApiError
   */
  public static getSessionTermPaymentDetails({
    schoolId,
    sessionId,
    termDefinitionId,
  }: {
    schoolId: string;
    sessionId: string;
    termDefinitionId: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
  }): CancelablePromise<SubscriptionPaymentDetailsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/session-term-payment-details',
      query: {
        schoolId: schoolId,
        sessionId: sessionId,
        termDefinitionId: termDefinitionId,
      },
    });
  }

  /**
   * @returns SubscriptionPlanResponse OK
   * @throws ApiError
   */
  public static getSubscriptionPlans({
    schoolId,
    sessionId,
    termDefinition,
  }: {
    schoolId: string;
    sessionId: string;
    termDefinition: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
  }): CancelablePromise<Array<SubscriptionPlanResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/plans',
      query: {
        schoolId: schoolId,
        sessionId: sessionId,
        termDefinition: termDefinition,
      },
    });
  }

  /**
   * @returns string OK
   * @throws ApiError
   */
  public static getNegotiationTypes(): CancelablePromise<
    Array<
      | 'STANDARD_PER_STUDENT'
      | 'SPLIT'
      | 'NON_STANDARD_PER_STUDENT'
      | 'FLAT_FEE_PER_TERM'
      | 'FLAT_FEE_PER_SESSION'
    >
  > {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/negotiation-types',
    });
  }

  /**
   * @returns SubscriptionInvoiceResponse OK
   * @throws ApiError
   */
  public static getInvoiceData({
    schoolId,
    sessionId,
    termDefinition,
  }: {
    schoolId: string;
    sessionId: string;
    termDefinition: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
  }): CancelablePromise<SubscriptionInvoiceResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/invoice-data',
      query: {
        schoolId: schoolId,
        sessionId: sessionId,
        termDefinition: termDefinition,
      },
    });
  }

  /**
   * @returns AllSchoolsSubscriptionPaymentsResponse OK
   * @throws ApiError
   */
  public static getFinancialData({
    sessionId,
    termDefinitionId,
    countryCode,
  }: {
    sessionId: string;
    termDefinitionId: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
    countryCode: string;
  }): CancelablePromise<AllSchoolsSubscriptionPaymentsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/financial-data',
      query: {
        sessionId: sessionId,
        termDefinitionId: termDefinitionId,
        countryCode: countryCode,
      },
    });
  }

  /**
   * @returns SubscriptionExportDataResponse OK
   * @throws ApiError
   */
  public static getSubscriptionsExport({
    sessionId,
    termDefinitionId,
    countryCode,
    search,
    pricingModel,
    planType,
    status,
  }: {
    sessionId: string;
    termDefinitionId: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
    countryCode: string;
    search?: string;
    pricingModel?:
      | 'STANDARD_PER_STUDENT'
      | 'SPLIT'
      | 'NON_STANDARD_PER_STUDENT'
      | 'FLAT_FEE_PER_TERM'
      | 'FLAT_FEE_PER_SESSION';
    planType?: 'FREE' | 'STARTER' | 'STANDARD' | 'PREMIUM';
    status?: 'ACTIVE' | 'EXPIRED';
  }): CancelablePromise<Array<SubscriptionExportDataResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/export',
      query: {
        sessionId: sessionId,
        termDefinitionId: termDefinitionId,
        countryCode: countryCode,
        search: search,
        pricingModel: pricingModel,
        planType: planType,
        status: status,
      },
    });
  }

  /**
   * @returns SubscriptionResponse OK
   * @throws ApiError
   */
  public static getSubscriptionDetails({
    schoolId,
    sessionId,
    termDefinitionId,
  }: {
    schoolId: string;
    sessionId: string;
    termDefinitionId: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
  }): CancelablePromise<SubscriptionResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/details',
      query: {
        schoolId: schoolId,
        sessionId: sessionId,
        termDefinitionId: termDefinitionId,
      },
    });
  }

  /**
   * @returns SubscriptionDashboardData OK
   * @throws ApiError
   */
  public static getSubscriptionDashboardData({
    sessionId,
    termDefinitionId,
    countryCode,
  }: {
    sessionId: string;
    termDefinitionId: 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH';
    countryCode: string;
  }): CancelablePromise<SubscriptionDashboardData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/dashboard-data',
      query: {
        sessionId: sessionId,
        termDefinitionId: termDefinitionId,
        countryCode: countryCode,
      },
    });
  }

  /**
   * @returns Country OK
   * @throws ApiError
   */
  public static getSubscriptionCountries(): CancelablePromise<Array<Country>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/countries',
    });
  }

  /**
   * @returns SubscriptionInvoiceItemResponse OK
   * @throws ApiError
   */
  public static getBillingHistory({
    schoolId,
    sessionId,
  }: {
    schoolId: string;
    sessionId: string;
  }): CancelablePromise<Array<SubscriptionInvoiceItemResponse>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/dashboard/subscription/billing-history',
      query: {
        schoolId: schoolId,
        sessionId: sessionId,
      },
    });
  }
}
